/** @jsx jsx */
import { Button } from '@datacamp/waffles/button';
import { Heading } from '@datacamp/waffles/heading';
import { mediaQuery } from '@datacamp/waffles/helpers';
import { useMediaQuery } from '@datacamp/waffles/hooks';
import { ArrowRight } from '@datacamp/waffles/icon';
import { Paragraph } from '@datacamp/waffles/paragraph';
import { darkThemeStyle } from '@datacamp/waffles/theme';
import { tokens } from '@datacamp/waffles/tokens';
import { jsx } from '@emotion/react';
import { FloatingFocusManager, useFloating } from '@floating-ui/react';
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { isFeatureEnabled } from '../../helpers/featureFlags';
import { useSelector } from '../../interfaces/State';
import { selectStreakDays, selectStreakWeekView } from '../../redux/selectors';

import { DoubleFreezeIcon } from './FreezeIcon';
import { StreakWidget } from './StreakWidget';

export type DailyStreakScreenProps = {
  onExit: () => void;
};

function useCopy(
  streakDays?: number,
): { continueLabel: string; headerText: string; hintText: string } {
  const { t } = useTranslation();

  if (!streakDays) {
    return {
      headerText: '',
      hintText: '',
      continueLabel: '',
    };
  }

  if (streakDays === 1) {
    return {
      headerText: `${t('DailyStreakModal.headerStarted')} ⚡️`,
      hintText: t('DailyStreakModal.hintFirstDay'),
      continueLabel: t('DailyStreakModal.cta'),
    };
  }

  if (streakDays < 7) {
    return {
      headerText: `${t('DailyStreakModal.headerFirstWeek')} 🚀`,
      hintText: t('DailyStreakModal.hintFirstWeek', { count: 7 - streakDays }),
      continueLabel: t('DailyStreakModal.cta'),
    };
  }

  return {
    headerText: `${t('DailyStreakModal.headerExtended')} 🚀`,
    hintText: t('DailyStreakModal.hintLongTerm'),
    continueLabel: t('DailyStreakModal.cta'),
  };
}

const FreezesAwarded: React.FC = () => {
  const { t } = useTranslation();

  return (
    <>
      <DoubleFreezeIcon />
      <Heading
        css={{
          fontSize: '32px',
        }}
        data-testid="freezes-awarded-header"
      >
        {t('DailyStreakModal.freezeOwnedHeader')}
      </Heading>
      <Paragraph
        css={{
          marginBottom: tokens.spacing.xlarge,
          fontSize: tokens.fontSizes.medium,
        }}
      >
        {t('DailyStreakModal.freezeAwardedMessage')}
      </Paragraph>
    </>
  );
};

const StreakDayReached: React.FC = () => {
  const streakDays = useSelector(selectStreakDays);
  const streakWeekView = useSelector(selectStreakWeekView);
  const { isAboveSmall } = useMediaQuery();

  const { headerText, hintText } = useCopy(streakDays);

  if (streakDays === undefined) {
    return null;
  }

  return (
    <>
      <StreakWidget
        isMobile={!isAboveSmall}
        streakLengthInDays={streakDays}
        streakWeekView={streakWeekView}
      />
      <Heading
        data-testid="streak-day-reached-header"
        css={{
          marginTop: tokens.spacing.xlarge,
          fontSize: '32px',
        }}
      >
        {headerText}
      </Heading>
      <Paragraph
        css={{
          marginBottom: tokens.spacing.xlarge,
        }}
      >
        {hintText}
      </Paragraph>
    </>
  );
};

const DailyStreakScreen: React.FC<DailyStreakScreenProps> = (props) => {
  const { onExit } = props;

  const { context } = useFloating();
  const autoFocusRef = useRef(null);

  const { isAboveSmall } = useMediaQuery();
  const streakDays = useSelector(selectStreakDays);

  const [isFreezeModalOpen, setIsFreezeModalOpen] = useState(false);
  const { continueLabel } = useCopy(streakDays);

  const hasFreezeAttributionEnabled = useMemo(() => {
    if (streakDays === undefined) {
      return false;
    }

    const hasStreakLogicEnabled = isFeatureEnabled('wl--streak-freezes');

    if (!hasStreakLogicEnabled) {
      return false;
    }

    return isFeatureEnabled('wl--streak-freeze-attribution');
  }, [streakDays]);

  if (streakDays === undefined) {
    return null;
  }

  const shouldShowFreezeModal =
    hasFreezeAttributionEnabled && (streakDays === 2 || streakDays % 5 === 0);

  const onContinue = () => {
    if (!shouldShowFreezeModal || isFreezeModalOpen) {
      onExit();
    } else {
      setIsFreezeModalOpen(true);
    }
  };

  return (
    <FloatingFocusManager
      context={context}
      initialFocus={autoFocusRef}
      modal
      closeOnFocusOut={false}
    >
      <div
        css={[
          darkThemeStyle,
          {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: tokens.colors.navy,
            zIndex: tokens.zIndex.modal,
            display: 'flex',
            flexDirection: 'column',
            padding: tokens.spacing.xlarge,
            justifyContent: 'center',
            alignItems: 'stretch',
          },
        ]}
      >
        <div
          css={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexGrow: 1,
            [mediaQuery.aboveSmall]: { flexGrow: 0 },
            flexDirection: 'column',
            textAlign: 'center',
          }}
        >
          {isFreezeModalOpen ? <FreezesAwarded /> : <StreakDayReached />}
        </div>
        <div
          css={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
          }}
        >
          <Button
            ref={autoFocusRef}
            variant="primary"
            iconRight={isAboveSmall ? <ArrowRight /> : undefined}
            fullWidth={!isAboveSmall}
            onClick={onContinue}
          >
            {continueLabel}
          </Button>
        </div>
      </div>
    </FloatingFocusManager>
  );
};

export default DailyStreakScreen;
